import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plane, History, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const AviatorPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [isPlaying, setIsPlaying] = useState(false);
  const [multiplier, setMultiplier] = useState(1.0);
  const [hasBet, setHasBet] = useState(false);
  const [currentBetId, setCurrentBetId] = useState(null);
  const [crashed, setCrashed] = useState(false);
  const [history, setHistory] = useState([]);
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const crashPointRef = useRef(null);

  // Generate random crash point
  const generateCrashPoint = () => {
    // House edge built in - crash between 1.0 and 100x
    const random = Math.random();
    if (random < 0.1) return 1.0 + Math.random() * 0.5; // 10% chance crash early
    if (random < 0.5) return 1.5 + Math.random() * 2; // 40% chance 1.5-3.5x
    if (random < 0.8) return 3 + Math.random() * 5; // 30% chance 3-8x
    if (random < 0.95) return 8 + Math.random() * 20; // 15% chance 8-28x
    return 28 + Math.random() * 72; // 5% chance big win
  };

  const drawGame = useCallback((mult, crashed) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);
    
    // Draw grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i < height; i += 50) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }
    
    // Draw curve
    ctx.beginPath();
    ctx.strokeStyle = crashed ? '#FF3B30' : '#00E701';
    ctx.lineWidth = 3;
    
    const progress = Math.min((mult - 1) / 10, 1);
    const curveHeight = height - 100;
    
    ctx.moveTo(50, height - 50);
    
    for (let x = 50; x <= 50 + progress * (width - 100); x++) {
      const normalX = (x - 50) / (width - 100);
      const y = height - 50 - Math.pow(normalX, 0.5) * curveHeight * progress;
      ctx.lineTo(x, y);
    }
    
    ctx.stroke();
    
    // Draw plane
    if (!crashed) {
      const planeX = 50 + progress * (width - 100);
      const normalX = progress;
      const planeY = height - 50 - Math.pow(normalX, 0.5) * curveHeight * progress;
      
      ctx.save();
      ctx.translate(planeX, planeY);
      ctx.rotate(-Math.PI / 6);
      ctx.fillStyle = '#00E701';
      ctx.font = '24px Arial';
      ctx.fillText('✈️', -12, 8);
      ctx.restore();
    } else {
      // Explosion effect
      ctx.fillStyle = '#FF3B30';
      ctx.font = '32px Arial';
      ctx.fillText('💥', width / 2, height / 2);
    }
    
    // Draw multiplier
    ctx.fillStyle = crashed ? '#FF3B30' : '#00E701';
    ctx.font = 'bold 64px Rajdhani, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${mult.toFixed(2)}x`, width / 2, height / 2);
    
    if (crashed) {
      ctx.fillStyle = '#FF3B30';
      ctx.font = 'bold 24px Rajdhani, sans-serif';
      ctx.fillText('CRASHED!', width / 2, height / 2 + 40);
    }
  }, []);

  const startGame = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }

    try {
      const response = await axios.post(`${API}/games/aviator/bet`, {
        game_type: "aviator",
        bet_amount: betAmount
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentBetId(response.data.bet_id);
      setHasBet(true);
      setIsPlaying(true);
      setCrashed(false);
      setMultiplier(1.0);
      crashPointRef.current = generateCrashPoint();
      
      await refreshBalance();
      
      // Start game loop
      let mult = 1.0;
      gameLoopRef.current = setInterval(() => {
        mult += 0.01 * (1 + mult * 0.1); // Accelerating growth
        setMultiplier(mult);
        drawGame(mult, false);
        
        if (mult >= crashPointRef.current) {
          clearInterval(gameLoopRef.current);
          setCrashed(true);
          setIsPlaying(false);
          setHasBet(false);
          drawGame(mult, true);
          
          // Record crash
          axios.post(`${API}/games/aviator/crash?bet_id=${currentBetId}`, {}, {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setHistory(prev => [{ multiplier: mult.toFixed(2), crashed: true }, ...prev.slice(0, 9)]);
          toast.error(`Crashed at ${mult.toFixed(2)}x!`);
        }
      }, 50);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place bet");
    }
  };

  const cashOut = async () => {
    if (!hasBet || !currentBetId) return;
    
    clearInterval(gameLoopRef.current);
    
    try {
      const response = await axios.post(
        `${API}/games/aviator/cashout?bet_id=${currentBetId}&multiplier=${multiplier}`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setIsPlaying(false);
      setHasBet(false);
      setHistory(prev => [{ multiplier: multiplier.toFixed(2), won: true, winnings: response.data.winnings }, ...prev.slice(0, 9)]);
      toast.success(`Cashed out at ${multiplier.toFixed(2)}x! Won ₹${response.data.winnings.toFixed(2)}`);
      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cash out");
    }
  };

  useEffect(() => {
    // Initial draw
    drawGame(1.0, false);
    
    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [drawGame]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/20 flex items-center justify-center">
            <Plane className="w-6 h-6 text-[#FF3B30]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="aviator-title">Aviator</h1>
            <p className="text-[#A1A1AA] text-sm">Cash out before the plane flies away!</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Canvas */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#1A1B1F] border border-white/[0.08] overflow-hidden">
              <canvas
                ref={canvasRef}
                width={800}
                height={400}
                className="w-full h-auto"
                data-testid="aviator-canvas"
              />
              
              {/* Controls */}
              <div className="p-6 border-t border-white/[0.08]">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount (₹)</label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      disabled={isPlaying}
                      className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                      data-testid="aviator-bet-input"
                    />
                  </div>
                  
                  <div className="flex gap-2 items-end">
                    {[50, 100, 500, 1000].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setBetAmount(amount)}
                        disabled={isPlaying}
                        className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                      >
                        {amount}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4">
                  {!isPlaying ? (
                    <Button
                      onClick={startGame}
                      disabled={betAmount <= 0 || betAmount > (user?.balance || 0)}
                      className="w-full h-14 bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-lg btn-glow"
                      data-testid="aviator-bet-btn"
                    >
                      PLACE BET
                    </Button>
                  ) : hasBet ? (
                    <Button
                      onClick={cashOut}
                      className="w-full h-14 bg-[#FF3B30] hover:bg-[#E63329] text-white font-bold text-lg animate-pulse"
                      data-testid="aviator-cashout-btn"
                    >
                      CASH OUT @ {multiplier.toFixed(2)}x (₹{(betAmount * multiplier).toFixed(2)})
                    </Button>
                  ) : (
                    <Button
                      disabled
                      className="w-full h-14 bg-[#52525B] text-white font-bold text-lg"
                    >
                      WAITING...
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* History */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">Recent Rounds</h3>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {history.length > 0 ? history.map((h, idx) => (
                  <div
                    key={idx}
                    className={`px-3 py-1 rounded-full text-sm font-mono font-bold ${
                      h.crashed ? 'bg-[#FF3B30]/20 text-[#FF3B30]' : 'bg-[#00E701]/20 text-[#00E701]'
                    }`}
                  >
                    {h.multiplier}x
                  </div>
                )) : (
                  <p className="text-[#52525B] text-sm">No rounds yet</p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">How to Play</h3>
              </div>
              
              <ul className="space-y-3 text-[#A1A1AA] text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#00E701]">1.</span>
                  Place your bet before the round starts
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E701]">2.</span>
                  Watch the multiplier increase
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00E701]">3.</span>
                  Cash out before it crashes!
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#FF3B30]">!</span>
                  If you don't cash out, you lose your bet
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
